import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import {
  getLatestExchangeRates,
  getHistoricalExchangeRates,
  findCurrencyItemIds,
  resetMarketData,
} from '../../services/marketAnalysisService';
import type { ItemExchangeRate } from '../../services/marketAnalysisService';

// Define the state type
interface MarketAnalysisState {
  // Latest exchange rates
  latestRates: ItemExchangeRate[];

  // Historical exchange rates for selected item/currency
  historicalRates: ItemExchangeRate[];

  // Selected item and currency IDs for historical view
  selectedItemId: string | null;
  selectedCurrencyId: string | null;

  // Common currency IDs (cached for convenience)
  currencyIds: {
    exaltedOrbId: string | null;
    chaosOrbId: string | null;
    divineOrbId: string | null;
  };

  // Loading states
  loading: {
    latestRates: boolean;
    historicalRates: boolean;
    resettingMarketData: boolean;
  };

  // Error states
  error: {
    latestRates: string | null;
    historicalRates: string | null;
  };
}

// Initial state
const initialState: MarketAnalysisState = {
  latestRates: [],
  historicalRates: [],
  selectedItemId: null,
  selectedCurrencyId: null,
  currencyIds: {
    exaltedOrbId: null,
    chaosOrbId: null,
    divineOrbId: null,
  },
  loading: {
    latestRates: false,
    historicalRates: false,
    resettingMarketData: false,
  },
  error: {
    latestRates: null,
    historicalRates: null,
  },
};

// Async thunk for fetching latest exchange rates
export const fetchLatestRates = createAsyncThunk(
  'marketAnalysis/fetchLatestRates',
  async ({ gameId, leagueId }: { gameId: string; leagueId: string }, { rejectWithValue }) => {
    try {
      const data = await getLatestExchangeRates(gameId, leagueId);

      // Extract currency IDs for convenience
      const currencyIds = findCurrencyItemIds(data);

      return {
        rates: data,
        currencyIds,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch latest exchange rates');
    }
  },
);

// Async thunk for fetching historical exchange rates
export const fetchHistoricalRates = createAsyncThunk(
  'marketAnalysis/fetchHistoricalRates',
  async (
    {
      itemId,
      currencyId,
      gameId,
      leagueId,
      startDate,
      endDate,
    }: {
      itemId: string;
      currencyId: string;
      gameId: string;
      leagueId: string;
      startDate: string;
      endDate?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const data = await getHistoricalExchangeRates(itemId, currencyId, gameId, leagueId, startDate, endDate);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch historical exchange rates');
    }
  },
);

// Async thunk for resetting market data
export const resetMarketDataThunk = createAsyncThunk(
  'marketAnalysis/resetMarketData',
  async (
    {
      gameId,
      leagueId,
    }: {
      gameId: string;
      leagueId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const result = await resetMarketData(gameId, leagueId);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to reset market data');
    }
  },
);

// Create the market analysis slice
const marketAnalysisSlice = createSlice({
  name: 'marketAnalysis',
  initialState,
  reducers: {
    // Set selected item and currency for historical view
    setSelectedItem(state, action: PayloadAction<string>) {
      state.selectedItemId = action.payload;
    },
    setSelectedCurrency(state, action: PayloadAction<string>) {
      state.selectedCurrencyId = action.payload;
    },

    // Clear selected item/currency
    clearSelection(state) {
      state.selectedItemId = null;
      state.selectedCurrencyId = null;
      state.historicalRates = [];
    },

    // Reset all errors
    clearErrors(state) {
      state.error.latestRates = null;
      state.error.historicalRates = null;
    },
  },
  extraReducers: builder => {
    // Handle fetchLatestRates
    builder
      .addCase(fetchLatestRates.pending, state => {
        state.loading.latestRates = true;
        state.error.latestRates = null;
      })
      .addCase(fetchLatestRates.fulfilled, (state, action) => {
        state.loading.latestRates = false;
        state.latestRates = action.payload.rates;

        // Update currency IDs
        state.currencyIds = {
          exaltedOrbId: action.payload.currencyIds.exaltedOrbId || null,
          chaosOrbId: action.payload.currencyIds.chaosOrbId || null,
          divineOrbId: action.payload.currencyIds.divineOrbId || null,
        };
      })
      .addCase(fetchLatestRates.rejected, (state, action) => {
        state.loading.latestRates = false;
        state.error.latestRates = (action.payload as string) || 'Failed to fetch latest exchange rates';
      });

    // Handle fetchHistoricalRates
    builder
      .addCase(fetchHistoricalRates.pending, state => {
        state.loading.historicalRates = true;
        state.error.historicalRates = null;
      })
      .addCase(fetchHistoricalRates.fulfilled, (state, action) => {
        state.loading.historicalRates = false;
        state.historicalRates = action.payload;
      })
      .addCase(fetchHistoricalRates.rejected, (state, action) => {
        state.loading.historicalRates = false;
        state.error.historicalRates = (action.payload as string) || 'Failed to fetch historical exchange rates';
      })

      // Handle resetMarketDataThunk
      .addCase(resetMarketDataThunk.pending, state => {
        state.loading.resettingMarketData = true;
        state.error.latestRates = null;
      })
      .addCase(resetMarketDataThunk.fulfilled, state => {
        state.loading.resettingMarketData = false;
        // We'll refresh the data separately via fetchLatestRates, don't need to modify state here
      })
      .addCase(resetMarketDataThunk.rejected, (state, action) => {
        state.loading.resettingMarketData = false;
        state.error.latestRates = (action.payload as string) || 'Failed to reset market data';
      });
  },
});

// Export actions
export const { setSelectedItem, setSelectedCurrency, clearSelection, clearErrors } = marketAnalysisSlice.actions;

// Export selectors
export const selectLatestRates = (state: RootState) => state.marketAnalysis.latestRates;
export const selectHistoricalRates = (state: RootState) => state.marketAnalysis.historicalRates;
export const selectSelectedItemId = (state: RootState) => state.marketAnalysis.selectedItemId;
export const selectSelectedCurrencyId = (state: RootState) => state.marketAnalysis.selectedCurrencyId;
export const selectCurrencyIds = (state: RootState) => state.marketAnalysis.currencyIds;
export const selectLoadingState = (state: RootState) => state.marketAnalysis.loading;
export const selectErrorState = (state: RootState) => state.marketAnalysis.error;

// Export the reducer
export default marketAnalysisSlice.reducer;
